import { supabase } from './supabaseClient';
import {
  TourProgress,
  TourPreferences,
  InitializeTourParams,
  UpdateTourProgressParams,
  CompleteTourParams,
  TourCompletionStats,
  TourError
} from '../types/onboardingTour';

/**
 * Service for managing onboarding tour state and persistence
 * Handles tour initialization, progress tracking, and completion
 */
export class OnboardingTourService {
  private static instance: OnboardingTourService;
  private readonly TOUR_VERSION = '1.0';
  private readonly STORAGE_KEY = 'onboarding_tour_preferences';

  private constructor() {}

  /**
   * Get singleton instance of OnboardingTourService
   */
  public static getInstance(): OnboardingTourService {
    if (!OnboardingTourService.instance) {
      OnboardingTourService.instance = new OnboardingTourService();
    }
    return OnboardingTourService.instance;
  }

  /**
   * Initialize tour for a user
   * Creates or resets tour progress in database
   */
  public async initializeTour(params: InitializeTourParams): Promise<string> {
    try {
      const { userId, tourVersion = this.TOUR_VERSION } = params;

      const { data, error } = await supabase.rpc('initialize_tour_progress', {
        p_user_id: userId,
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('INIT_FAILED', 'Failed to initialize tour', error);
      }

      return data;
    } catch (error) {
      console.error('Error initializing tour:', error);
      throw error instanceof Error ? error : this.createTourError('INIT_ERROR', 'Unknown initialization error');
    }
  }

  /**
   * Update tour progress for a user
   * Persists current step, completed steps, and time spent
   */
  public async updateTourProgress(params: UpdateTourProgressParams): Promise<boolean> {
    try {
      const { 
        userId, 
        currentStep, 
        completedSteps, 
        skippedSteps, 
        timeSpent = 0,
        tourVersion = this.TOUR_VERSION 
      } = params;

      const { data, error } = await supabase.rpc('update_tour_progress', {
        p_user_id: userId,
        p_current_step: currentStep,
        p_completed_steps: completedSteps,
        p_skipped_steps: skippedSteps,
        p_time_spent: timeSpent,
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('UPDATE_FAILED', 'Failed to update tour progress', error);
      }

      return data;
    } catch (error) {
      console.error('Error updating tour progress:', error);
      throw error instanceof Error ? error : this.createTourError('UPDATE_ERROR', 'Unknown update error');
    }
  }

  /**
   * Complete tour for a user
   * Marks tour as completed with specified completion method
   */
  public async completeTour(params: CompleteTourParams): Promise<boolean> {
    try {
      const { userId, completionMethod, tourVersion = this.TOUR_VERSION } = params;

      const { data, error } = await supabase.rpc('complete_tour', {
        p_user_id: userId,
        p_completion_method: completionMethod,
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('COMPLETE_FAILED', 'Failed to complete tour', error);
      }

      return data;
    } catch (error) {
      console.error('Error completing tour:', error);
      throw error instanceof Error ? error : this.createTourError('COMPLETE_ERROR', 'Unknown completion error');
    }
  }

  /**
   * Check if user has already seen the tour
   * Returns true if user has started or completed the tour
   */
  public async hasUserSeenTour(userId: string, tourVersion: string = this.TOUR_VERSION): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_user_seen_tour', {
        p_user_id: userId,
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('CHECK_FAILED', 'Failed to check tour status', error);
      }

      return data;
    } catch (error) {
      console.error('Error checking tour status:', error);
      // Return false on error to allow tour to show (fail-safe)
      return false;
    }
  }

  /**
   * Get current tour progress for a user
   * Returns detailed progress information
   */
  public async getTourProgress(userId: string, tourVersion: string = this.TOUR_VERSION): Promise<TourProgress | null> {
    try {
      const { data, error } = await supabase.rpc('get_user_tour_progress', {
        p_user_id: userId,
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('FETCH_FAILED', 'Failed to fetch tour progress', error);
      }

      if (!data || data.length === 0) {
        return null;
      }

      const progress = data[0];
      return {
        id: progress.id,
        user_id: userId,
        tour_version: tourVersion,
        current_step: progress.current_step,
        completed_steps: progress.completed_steps || [],
        skipped_steps: progress.skipped_steps || [],
        is_completed: progress.is_completed,
        completion_method: progress.completion_method,
        started_at: new Date(progress.started_at),
        completed_at: progress.completed_at ? new Date(progress.completed_at) : undefined,
        last_interaction_at: new Date(progress.last_interaction_at),
        total_time_spent: progress.total_time_spent,
        created_at: new Date(progress.started_at), // Using started_at as created_at
        updated_at: new Date(progress.last_interaction_at) // Using last_interaction_at as updated_at
      };
    } catch (error) {
      console.error('Error fetching tour progress:', error);
      return null;
    }
  }

  /**
   * Get tour completion statistics
   * Returns aggregated statistics for analytics
   */
  public async getTourCompletionStats(tourVersion: string = this.TOUR_VERSION): Promise<TourCompletionStats | null> {
    try {
      const { data, error } = await supabase.rpc('get_tour_completion_stats', {
        p_tour_version: tourVersion
      });

      if (error) {
        throw this.createTourError('STATS_FAILED', 'Failed to fetch tour statistics', error);
      }

      if (!data || data.length === 0) {
        return null;
      }

      const stats = data[0];
      return {
        total_started: stats.total_started,
        total_completed: stats.total_completed,
        total_skipped: stats.total_skipped,
        total_abandoned: stats.total_abandoned,
        completion_rate: parseFloat(stats.completion_rate) || 0,
        average_time_spent: parseFloat(stats.average_time_spent) || 0
      };
    } catch (error) {
      console.error('Error fetching tour statistics:', error);
      return null;
    }
  }

  /**
   * Save user tour preferences to local storage
   * Manages user preferences for tour behavior
   */
  public saveTourPreferences(preferences: TourPreferences): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving tour preferences:', error);
    }
  }

  /**
   * Load user tour preferences from local storage
   * Returns default preferences if none exist
   */
  public loadTourPreferences(): TourPreferences {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading tour preferences:', error);
    }

    // Return default preferences
    return {
      autoStart: true,
      showAnimations: true,
      enableSounds: false,
      preferredSpeed: 'normal',
      skipIntroduction: false
    };
  }

  /**
   * Reset tour progress for a user
   * Allows user to restart the tour
   */
  public async resetTourProgress(userId: string, tourVersion: string = this.TOUR_VERSION): Promise<string> {
    try {
      // Initialize tour will reset existing progress
      return await this.initializeTour({ userId, tourVersion });
    } catch (error) {
      console.error('Error resetting tour progress:', error);
      throw error instanceof Error ? error : this.createTourError('RESET_ERROR', 'Unknown reset error');
    }
  }

  /**
   * Check if tour should auto-start for user
   * Considers user preferences and tour status
   */
  public async shouldAutoStartTour(userId: string): Promise<boolean> {
    try {
      const preferences = this.loadTourPreferences();
      if (!preferences.autoStart) {
        return false;
      }

      const hasSeenTour = await this.hasUserSeenTour(userId);
      return !hasSeenTour;
    } catch (error) {
      console.error('Error checking auto-start conditions:', error);
      return false;
    }
  }

  /**
   * Create standardized tour error
   */
  private createTourError(code: string, message: string, details?: any): TourError {
    return {
      code,
      message,
      details: details ? { originalError: details } : undefined
    };
  }

  /**
   * Get current tour version
   */
  public getCurrentTourVersion(): string {
    return this.TOUR_VERSION;
  }

  /**
   * Validate tour step number
   */
  public isValidStepNumber(stepNumber: number, totalSteps: number): boolean {
    return stepNumber >= 0 && stepNumber < totalSteps;
  }

  /**
   * Calculate tour progress percentage
   */
  public calculateProgressPercentage(currentStep: number, totalSteps: number): number {
    if (totalSteps === 0) return 0;
    return Math.round((currentStep / totalSteps) * 100);
  }

  /**
   * Format time spent in human-readable format
   */
  public formatTimeSpent(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m ${seconds % 60}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }
}

// Export singleton instance
export const onboardingTourService = OnboardingTourService.getInstance();